/**
 * collectionRemove.ts — "Remove from your collection", for real.
 *
 * WHERE IT IS (card 1218941367836566, Blake 2026-09-28): at the bottom of
 * both edit screens, Quick Edit and the full edit, as a `[data-mgract=
 * "remove:<row id>"]` button. It left the tile's action list, on every
 * device, when a phone's tap on a tile started opening the actions pop-up:
 * Remove one tap from Edit, under the finger, was the wrong neighbor. The
 * listing page's ⋯ "Delete listing" folded into it the same day, so there is
 * one way to remove an item. The value is still "remove:" plus the row's id,
 * so the write guard's branch and this module cover it wherever it sits. Edit
 * offers it only for a row Remove can act on (active, or in the collection):
 * a pending, sold or traded piece is locked, and the server refuses it.
 *
 * WHAT WAS WRONG (card 1218907631345203, parent 1218886330579015). The
 * collection card's Remove (app.js, the "remove:" branch of the [data-mgract]
 * handler) took two taps and then spliced MY_COLLECTION and toasted "Removed
 * from your collection" with no request at all. MY_COLLECTION is real data
 * (installFloorListings fills it from GET /api/collections), so the piece came
 * back on the next load while the visitor believed it gone. It lied with
 * nothing guarding it: in neither of writeGuard's lists, and carved out of the
 * derivation test that exists to catch exactly this. (Looking For's "+ Add"
 * and its ✕ do the same to the wants list; they are carded on their own, card
 * 1217187414504334.) It is now a GUARDED_BRANCHES entry (the fail-safe) and
 * this module does the real write alongside, the way wizardPublish does.
 *
 * 🔴 THE ROUTE FOLLOWS THE APP'S, AND THE ID SPACE FAILS SILENTLY. The app's
 * grid (profile.tsx) sends a row that is one of your listings to
 * DELETE /api/listings/:id and a standalone piece to DELETE /api/collections/:id.
 * This does the same, keyed on whether the row carries a listing (`lid`):
 *
 *   a row with a listing  -> DELETE /api/listings/:lid, as the app does
 *                            (a 404: the listing is already gone, which is the
 *                            end state wanted, so it counts as removed;
 *                            a 403 on a real collection row: the listing is
 *                            someone else's, and only our row is ours to
 *                            remove, so DELETE /api/collections/:id)
 *   a piece never listed  -> DELETE /api/collections/:id
 *
 * Why not the collections route for every real row, though it also takes the
 * linked listing down: when that takedown throws, the route logs it, deletes the
 * row anyway and answers { ok: true }, so the page would say "Removed" over a
 * listing still for sale (adversarial review, 2026-09-27; a server fix for Nick).
 * The app never sends a listing down that path, so this does not either. What
 * the listings route leaves is the linked collection row, now pointing at a
 * removed listing: GET /api/collections returns it with that status and both the
 * website and the app drop it, so nothing reappears.
 *
 * And the id itself: not every row's `id` is a user_collections key. GET
 * /api/collections synthesizes "listing:" plus the listing's id for a listing
 * with no collection row, and the floor derives "my-" rows from the owner's own
 * listings when that call failed or left one unclaimed. The collections route
 * finds no row for either, deletes nothing, and still answers { ok: true }, a
 * success that did nothing. Both carry `lid`, so both take the listings route,
 * and one without it has nothing any route can remove.
 *
 * 🪤 THE CONFIRM IS REBUILT HERE, NOT BORROWED. The guard stops the click in
 * capture before Blake's handler ever runs, so his arm ("Tap again to remove",
 * back to the idle label after 2.6 s) never happens under it. This keeps his
 * interaction exactly, the armed label and timing included, and sends only on
 * the second tap. The idle label is the BUTTON'S OWN, read when it arms: the
 * edit screens say "Remove from your collection", and a button restored to a
 * word it never carried would be a different control after one tap.
 *
 * WHAT A PERSON SEES. The piece leaves only when the server says yes, with his
 * own "Removed from your collection", which is now true, and the edit it was
 * removed from is over: the bridge closes Quick Edit or the full edit and, while
 * /web is the page in front of them, lands on your collection (#/profile), where
 * the tile is gone, the message shown after that landing's paint. If the answer
 * lands after they left /web, the edit still ends, the hidden page repaints in
 * place and the message goes into its toast at once. A refusal (the listing has
 * a live offer, or is part of a transaction) shows the server's own sentence,
 * and you stay in the edit, the button back to its label.
 */

export const REMOVE_ATTR = "data-mgract";
export const REMOVE_PREFIX = "remove:";
/** The button's label on both edit screens (app.js, renderEditListingModal
 *  and renderWizard); a button arms from whatever it carries and goes back to
 *  that, so this is pinned for the tests, not read by the module. */
export const REMOVE_LABEL = "Remove from your collection";
/** Blake's own armed label and timing (app.js, the remove branch). */
export const ARMED_LABEL = "Tap again to remove";
export const ARM_MS = 2600;
/** Blake's own words, true now that the server did it. */
export const REMOVED_MESSAGE = "Removed from your collection";
/** Only for a failure the server gave no sentence for. */
export const GENERIC_FAILURE_MESSAGE = "Couldn't remove that — please try again";
/** A 401 here is an expired session: the button only renders on your own
 *  profile. Worded like profileEdit's and listingEdit's owner-screen lines. */
export const SIGNIN_MESSAGE = "Sign in again to remove this item.";
/** The button names a row the page does not hold, or one no route can delete. */
export const UNRESOLVED_MESSAGE = "We can't tell which item this is — reload the page and try again";

/** The floor's and the server's stand-ins for a listing with no collection row. */
const SYNTHETIC_PREFIXES = ["listing:", "my-"];

export interface RemovePlan {
  url: string;
  kind: "listing" | "collection";
  /** A real row's own route, tried when the listing turns out not to be ours (403). */
  ownRowUrl: string | null;
}

/**
 * Which request removes this row, or null when none can. A pure function, so
 * the routing rule is measured without a DOM.
 */
export function removePlanFor(row: { id: string; lid: string | null }): RemovePlan | null {
  const synthetic = SYNTHETIC_PREFIXES.some((p) => row.id.startsWith(p));
  const ownRowUrl = synthetic ? null : `/api/collections/${encodeURIComponent(row.id)}`;
  if (row.lid) return { url: `/api/listings/${encodeURIComponent(row.lid)}`, kind: "listing", ownRowUrl };
  return ownRowUrl ? { url: ownRowUrl, kind: "collection", ownRowUrl: null } : null;
}

/** The value after the FIRST colon: a synthetic id carries one of its own. */
export function removeIdFrom(value: string | null): string | null {
  if (!value || !value.startsWith(REMOVE_PREFIX)) return null;
  const id = value.slice(REMOVE_PREFIX.length);
  return id || null;
}

type RemoveWindow = Window & {
  toast?: (message: string) => void;
  __gbzCollectionRow?: (id: string) => { id: string; lid: string | null } | null;
  __gbzRemoveCollectionRow?: (id: string) => boolean | "navigating";
};

export function installCollectionRemove(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  {
    notify = (message: string) => {
      if (!win.document.getElementById("toast")) return;
      (win as RemoveWindow).toast?.(message);
    },
  }: { notify?: (m: string) => void } = {},
): () => void {
  /* Armed buttons, each with its revert timer and the label it carried before
     it armed. A WeakMap, because a repaint replaces the element and an armed
     button that left the page must not keep a timer's reference alive. */
  const armed = new WeakMap<Element, { timer: number; label: string }>();
  /* In flight, per row: with a real await behind the second tap, a third tap
     would send the delete twice. */
  const busy = new Set<string>();
  /* The one-shot listener for a landing on #/profile, kept so the teardown
     can drop it. It checks the hash itself: the install layer's cross-route
     guard swallows hashchange while the visitor is off /web, so a landing
     that fired there never reaches it, and a listener left waiting must not
     say "Removed" on some later, unrelated navigation. */
  let landing: (() => void) | null = null;
  const dropLanding = (): void => {
    if (landing) win.removeEventListener("hashchange", landing);
    landing = null;
  };

  const disarm = (button: Element): void => {
    const arm = armed.get(button);
    if (!arm) return;
    win.clearTimeout(arm.timer);
    armed.delete(button);
    button.textContent = arm.label;
  };

  async function remove(button: Element, id: string): Promise<void> {
    const row = (win as RemoveWindow).__gbzCollectionRow?.(id) ?? null;
    const plan = row ? removePlanFor(row) : null;
    if (!plan) {
      disarm(button);
      notify(UNRESOLVED_MESSAGE);
      return;
    }

    let res: Response;
    try {
      res = await authFetch(plan.url, { method: "DELETE" });
      if (plan.kind === "listing" && res.status === 403 && plan.ownRowUrl) {
        res = await authFetch(plan.ownRowUrl, { method: "DELETE" });
      }
    } catch {
      disarm(button);
      notify(GENERIC_FAILURE_MESSAGE);
      return;
    }

    /* Already gone: someone removed it in another tab or in the app. */
    const gone = plan.kind === "listing" && res.status === 404;
    if (res.status === 401) {
      disarm(button);
      notify(SIGNIN_MESSAGE);
      return;
    }
    if (!res.ok && !gone) {
      /* The server's own words when it gave any: "This listing has an active
         offer. Resolve or cancel the offer before deleting it." and "This
         listing is part of an active or completed transaction and cannot be
         deleted" are the two a seller meets. A 500 can be an HTML page, so the
         parse must never throw inside a capture listener. */
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      disarm(button);
      notify(body.error || GENERIC_FAILURE_MESSAGE);
      return;
    }

    /* The server removed it: the closet and the floor follow, the edit it was
       removed from ends, and the page lands on your collection (the bridge),
       or repaints in place when /web is not the page in front of them.
       🪤 ON A LANDING, THE MESSAGE WAITS FOR IT. His render for the new route
       paints a fresh, empty toast node (wizardPublish's rule 3 records the
       wipe), so a toast shown before the hashchange paint lives for a few
       milliseconds. hashchange listeners run in the order they were added and
       his render was added at load, so one added now runs after the paint. */
    const outcome = (win as RemoveWindow).__gbzRemoveCollectionRow?.(id);
    if (outcome !== "navigating") { notify(REMOVED_MESSAGE); return; }
    dropLanding();
    landing = () => {
      dropLanding();
      if (win.location.hash === "#/profile") notify(REMOVED_MESSAGE);
    };
    win.addEventListener("hashchange", landing);
  }

  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    const button = target?.closest?.(`[${REMOVE_ATTR}]`);
    if (!button) return;
    const id = removeIdFrom(button.getAttribute(REMOVE_ATTR));
    if (!id) return; // another branch: edit:, listit:, order:, sale:, trade:

    if (!armed.has(button)) {
      const label = button.textContent ?? "";
      button.textContent = ARMED_LABEL;
      armed.set(button, { timer: win.setTimeout(() => disarm(button), ARM_MS), label });
      return;
    }
    if (busy.has(id)) return;
    win.clearTimeout(armed.get(button)!.timer);
    busy.add(id);
    void remove(button, id).finally(() => busy.delete(id));
  };

  win.document.addEventListener("click", onClick, true);
  return () => {
    win.document.removeEventListener("click", onClick, true);
    dropLanding();
  };
}
